import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";

const schema = z.object({ email: z.string().trim().email("Invalid email").max(255) });

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.resetPasswordForEmail(parsed.data.email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setSent(true);
    toast.success("Check your email for a reset link");
  };

  return (
    <MobileShell>
      <Helmet>
        <title>Reset your password — Cash Isle</title>
        <meta name="description" content="Reset your Cash Isle password. Enter your email and we'll send a secure reset link." />
        <link rel="canonical" href="https://cash-isle-hustle-hub.lovable.app/forgot-password" />
        <meta name="robots" content="noindex" />
      </Helmet>
      <BrandHeader onBack={() => navigate("/auth")} curve />
      <div className="-mt-6 mx-4 bg-background rounded-2xl shadow-card px-6 pt-7 pb-8 flex-1 animate-fade-in-up">
        <h1 className="font-display text-3xl font-bold text-center mb-2">Forgot password?</h1>
        <p className="text-center text-muted-foreground text-sm mb-6">
          Enter your email and we'll send you a reset link.
        </p>
        {sent ? (
          <div className="space-y-4 text-center">
            <p className="text-sm">
              If an account exists for <strong>{email}</strong>, a reset link is on its way.
            </p>
            <Button variant="hero" size="xl" className="w-full" onClick={() => navigate("/auth")}>
              Back to Log In
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 pl-12 rounded-xl bg-secondary/60 border-transparent focus-visible:bg-background focus-visible:border-primary"
              />
            </div>
            <Button type="submit" variant="hero" size="xl" className="w-full mt-4" disabled={loading}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
        )}
      </div>
    </MobileShell>
  );
};

export default ForgotPassword;
